import PicksSidebarAd from "@components/ads/PicksSidebarAd";
import AnnouncementBanner from "@components/AnnouncementBanner";
import DriverModal from "@components/DriverModal";
import EntryBar from "@components/EntryBar";
import { ContentSection } from "@components/LayoutProvider";
import MemberRoundColumn from "@components/Leagues/MemberRoundColumn";
import MobileRoundSelector from "@components/MobileRoundSelector";
import PicksCompleteModal from "@components/PicksCompleteModal";
import PrePicksOverlay from "@components/PrePicksOverlay";
import RoundColumn from "@components/RoundColumn";
import useAnalytics from "@hooks/useAnalytics";
import useGame from "@hooks/useGame";
import { PicksProvider } from "@hooks/usePicksContext";
import Cookies from "js-cookie";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import React, { Children, useEffect, useState } from "react";
import MediaQuery from "react-responsive";
import settings from "src/settings";
import { breakpoints, media, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
const { COOKIE_NAMES } = settings;
const { localAnnouncements } = COOKIE_NAMES;

const EntryBarWrapper = styled.div`
  margin-top: ${pxToRem(23)};
  padding: 0 1rem;
  display: flex;

  ${media.tablet} {
    margin: 0;
    padding: 0;
  }
`;

const SectionWrapper = styled.div`
  position: relative;
`;

const Section = styled(ContentSection)`
  margin: ${pxToRem(40)} 0 ${pxToRem(20)};
  padding: 0 1rem ${pxToRem(20)};
  display: flex;
  overflow-x: auto;
  gap: ${pxToRem(5)};

  ${media.tablet} {
    margin-top: ${pxToRem(12)};
    padding: 0 ${pxToRem(17)};
    justify-content: space-evenly;
    gap: 0;
  }
  ${media.mobileMid} {
    justify-content: space-around;
    gap: 0;
  }
`;

const BackToLeague = styled.div`
  & button {
    padding: ${pxToRem(5)};
    background: none;
    border: none;
    color: white;
    text-transform: uppercase;
    text-decoration: none;
    font-size: ${pxToRem(12)};
    letter-spacing: 0px;
    transition: color 0.3s ease-out;
    margin-top: ${pxToRem(10)};

    &:focus,
    :focus-within {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.blueAlt};
    }

    &:not(:disabled) {
      &.active,
      :hover {
        color: ${({ theme }) => theme.gold};
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.18;
    }
  }
`;

export default function Picks({ leagueMemberEntry, isLeagueMemberView, leagueId }) {
  const { data: game } = useGame();
  const { trackPage } = useAnalytics();
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);

  useEffect(() => {
    setDismissedAnnouncements(Cookies.get(localAnnouncements));
  }, []);

  useEffect(() => {
    trackPage("picks:index");
  }, [trackPage]);

  function handleDismiss(id) {
    const tempAnnouncementArr = dismissedAnnouncements ? [dismissedAnnouncements] : [];
    tempAnnouncementArr.push(id);
    setDismissedAnnouncements(tempAnnouncementArr);
    Cookies.set(localAnnouncements, tempAnnouncementArr);
  }

  return (
    <PicksProvider>
      {game?.announcements &&
        game?.announcements.map((announcement) => {
          if (!dismissedAnnouncements || !dismissedAnnouncements.includes(announcement.id)) {
            return (
              <AnnouncementBanner
                key={announcement.id}
                id={announcement.id}
                message={announcement.message}
                handleDismiss={handleDismiss}
              />
            );
          } else {
            return;
          }
        })}
      {isLeagueMemberView && (
        <BackToLeague>
          <NavLink href={`/leagues/${leagueId}`}>
            <button>Back to League</button>
          </NavLink>
        </BackToLeague>
      )}
      <EntryBarWrapper>
        <EntryBar isLeagueMemberView={isLeagueMemberView} leagueMemberEntry={leagueMemberEntry} />
        <PicksSidebarAd />
      </EntryBarWrapper>
      <MobileRoundSelector />
      <SectionWrapper>
        <Section>
          <MediaQuery minWidth={Number(breakpoints.tablet.replace("px", ""))}>
            {!isLeagueMemberView ? (
              <RoundColumn roundId={0} isTabletCeil={false} />
            ) : (
              <MemberRoundColumn
                roundId={0}
                isTabletCeil={false}
                leagueMemberEntry={leagueMemberEntry}
              />
            )}
            {game.rounds.map((r) => {
              return !isLeagueMemberView ? (
                <RoundColumn key={r.round_id} roundId={r.round_id} isTabletCeil={false} />
              ) : (
                <MemberRoundColumn
                  key={r.round_id}
                  roundId={r.round_id}
                  isTabletCeil={false}
                  leagueMemberEntry={leagueMemberEntry}
                />
              );
            })}
          </MediaQuery>
          <MediaQuery maxWidth={Number(breakpoints.tablet.replace("px", "")) - 1}>
            {!isLeagueMemberView ? (
              <RoundColumn roundId={0} isTabletCeil={true} />
            ) : (
              <MemberRoundColumn
                roundId={0}
                isTabletCeil={true}
                leagueMemberEntry={leagueMemberEntry}
              />
            )}
            {game.rounds.map((r) => {
              return !isLeagueMemberView ? (
                <RoundColumn key={r.round_id} roundId={r.round_id} isTabletCeil={true} />
              ) : (
                <MemberRoundColumn
                  key={r.round_id}
                  roundId={r.round_id}
                  isTabletCeil={true}
                  leagueMemberEntry={leagueMemberEntry}
                />
              );
            })}
          </MediaQuery>
        </Section>
        <PrePicksOverlay />
      </SectionWrapper>
      <DriverModal />
      <PicksCompleteModal />
    </PicksProvider>
  );
}

function NavLink({ children, activeClassName = "active", ...props }) {
  const { asPath } = useRouter();
  const child = Children.only(children);
  const childClassName = child?.props?.className || "";

  const className =
    asPath === props.href || asPath === props.as
      ? `${childClassName} ${activeClassName}`.trim()
      : childClassName;

  return (
    <Link {...(props as LinkProps)}>
      {React.cloneElement(child, {
        className: className || null,
      })}
    </Link>
  );
}
